import { Box, Stack } from '@mantine/core';

import type { PreferenceOption } from '@repo/common/constants/integration/integrations.preferences';
import { IntegrationPreferenceType } from '@repo/common/constants/integration/integrations.preferences';
import {
	isPreviewIntegration,
	isQueryBlockIntegration,
	isWarehouseOrDatabaseIntegration,
} from '@repo/common/constants/integration/integrations.utils';
import { useMemo } from 'react';
import type { IIntegration } from '../../../../../api';
import { useUserList } from '../../../../../api';
import { useExtendedUserList } from '../../../../../api/hooks/user/useExtendedUserList';
import type { IntegrationSpec } from '../../../../../interfaces/IntegrationSpec';
import { useFeatureFlags } from '../../../../../utils/featureFlags/useFeatureFlags.ts';
import { AccessRequestPreferences } from './AccessRequestPreferences.tsx';
import { FiltersPreferences } from './FiltersPreferences.tsx';
import { GenericOptionsSwitches } from './GenericOptionsSwitches.tsx';
import { GithubPreferences } from './GithubPreferences.tsx';
import { GleanPreferences } from './GleanPreferences';
import { ImportMetadataPreferences } from './ImportMetadataPreferences.tsx';
import { Popularity } from './Popularity';
import { PowerBiPreferences } from './PowerBiPreferences.tsx';
import { PreviewPermissionsPreferences } from './PreviewPermissionsPreferences.tsx';
import { QueryPermissions } from './QueryPermissions';
import { QueryPermissionsSettings } from './QueryPermissionsSettings';
import { QueryPermissionsV2 } from './QueryPermissionsV2';
import { SsrsPreferences } from './SsrsPreferences.tsx';
import { UserLevelCredentials } from './UserLevelCredentials';

interface PreferencesPanelProps {
	integration: IIntegration;
	spec: IntegrationSpec;
	options: PreferenceOption[];
}

export function PreferencesPanel({
	integration,
	spec,
	options,
}: PreferencesPanelProps) {
	const { executeAccessV2, userLevelCredentials } = useFeatureFlags();

	const { activeUsers } = useExtendedUserList();
	const { data: serviceAccounts } = useUserList({
		filters: {
			is_service_account: true,
		},
		options: {
			select: ({ results }) => results,
		},
	});

	const isBuiltIn = spec.type === 'builtin';
	const isMarketplace = spec.type === 'marketplace';
	const isGithub = integration.type === 'github';
	const isGlean = integration.type === 'glean';
	const isPowerBi = integration.type === 'powerbi';
	const isSsrs = integration.type === 'ssrs';

	const hasQueryBlocks =
		(isBuiltIn && isQueryBlockIntegration(spec.value)) || isMarketplace;

	const hasPermissions =
		isBuiltIn &&
		(isQueryBlockIntegration(spec.value) || isPreviewIntegration(spec.value));

	const hasUsers = activeUsers && activeUsers.length > 0;
	const hasServiceAccounts = serviceAccounts && serviceAccounts.length > 0;

	const hasPopularity =
		isBuiltIn && isWarehouseOrDatabaseIntegration(spec.value);

	const supportsUserCredentials =
		isBuiltIn && spec.value.userLevelCredentialsFields?.length;
	const metadataManagementPreferences = useMemo(
		() =>
			options.filter(
				(option) =>
					option.type === IntegrationPreferenceType.METADATA_MANAGEMENT
			),
		[options]
	);
	const metadataPushPreferences = useMemo(
		() =>
			options.filter(
				(option) => option.type === IntegrationPreferenceType.METADATA_PUSH
			),
		[options]
	);
	const resourceManagementPreferences = useMemo(
		() =>
			options.filter(
				(option) =>
					option.type === IntegrationPreferenceType.RESOURCE_MANAGEMENT
			),
		[options]
	);

	const showDataAccessRequest =
		spec.type === 'builtin' && spec.value.supportsDataAccessRequests;

	return (
		<Stack spacing="4xl">
			{showDataAccessRequest && (
				<AccessRequestPreferences integration={integration} />
			)}
			{userLevelCredentials &&
				supportsUserCredentials &&
				spec.type === 'builtin' &&
				spec.value.supportsOAuthForUserCredentials && (
					<QueryPermissionsSettings integration={integration} />
				)}
			{userLevelCredentials &&
				supportsUserCredentials &&
				!(
					spec.type === 'builtin' && spec.value.supportsOAuthForUserCredentials
				) && <UserLevelCredentials integration={integration} />}
			{executeAccessV2 && hasUsers && hasQueryBlocks && (
				<QueryPermissionsV2 integration={integration} />
			)}
			{!executeAccessV2 && hasPermissions && (
				<PreviewPermissionsPreferences integration={integration} />
			)}
			{!executeAccessV2 && hasUsers && hasQueryBlocks && (
				<QueryPermissions integration={integration} />
			)}
			{hasPopularity && hasServiceAccounts && (
				<Popularity integration={integration} />
			)}
			{isPowerBi && <PowerBiPreferences integration={integration} />}
			{isSsrs && <SsrsPreferences integration={integration} />}
			{metadataManagementPreferences.length > 0 && !isGithub && !isGlean && (
				<ImportMetadataPreferences
					integration={integration}
					metadataManagementPreferences={metadataManagementPreferences}
				/>
			)}
			{metadataPushPreferences.length > 0 && !isGithub && !isGlean && (
				<GenericOptionsSwitches
					integration={integration}
					options={metadataPushPreferences}
					title="Metadata push"
					description="Select which metadata should be pushed back to the source system"
					withConfirmation
				/>
			)}
			{resourceManagementPreferences && !isGithub && !isGlean && (
				<GenericOptionsSwitches
					integration={integration}
					options={resourceManagementPreferences}
					title="Resource management"
					description="Select how queries, schemas and archived resources are managed"
				/>
			)}
			{!isGithub && !isGlean && (
				<Box pb="5xl">
					<FiltersPreferences integration={integration} />
				</Box>
			)}
			{isGithub && (
				<>
					<GithubPreferences integration={integration} />
					<GenericOptionsSwitches
						integration={integration}
						options={resourceManagementPreferences}
						title="DBT model update preferences"
						description="Select how changes in Secoda are applied to DBT models"
					/>
				</>
			)}
			{isGlean && <GleanPreferences />}
		</Stack>
	);
}
